import React from 'react';
import { TagsFilter } from './TagsFilter';
import { useTags } from '../../../features/settings/tags/hooks/useTags';
import AgentsFilter from './AgentsFilter';
import { useUsers } from '../../../contexts/users/UsersContext';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import DateRangePicker, { DateRange } from './DateRangePicker';
import { cn } from '@/lib/utils';

export interface Filters {
    stage: string;
    tags: string[];
    dateRange: DateRange;
    agents: string[];
    noTasks: boolean;
    overdueTasks: boolean;
    statusWon: boolean;
    statusLost: boolean;
}

interface FilterBarProps {
    onFilterChange: (filters: Filters) => void;
    /** Mantido na API por compatibilidade com callers existentes. */
    selectedFunnelId?: string | null;
    currentFilters: Filters;
}

const FilterBar: React.FC<FilterBarProps> = ({ onFilterChange, currentFilters }) => {
    const { tags } = useTags();
    const { crmUsers } = useUsers();
    const { user } = useAuth();
    const { canFilterByAgent } = usePermissions();

    const handleDateChange = (range: DateRange) => {
        onFilterChange({ ...currentFilters, dateRange: range });
    };

    const handleTagsChange = (tagIds: string[]) => {
        onFilterChange({ ...currentFilters, tags: tagIds });
    };

    const handleAgentsChange = (agentIds: string[]) => {
        onFilterChange({ ...currentFilters, agents: agentIds });
    };

    const displayAgentIds = currentFilters.agents.filter((a) => a !== '__unassigned__');
    const columns = canFilterByAgent ? 3 : 2;

    return (
        <nav
            aria-label="Filtros de tags, período e responsável"
            className={cn(
                'grid w-full min-w-0 divide-x divide-border overflow-hidden rounded-xl border border-border bg-card',
                columns === 3 ? 'grid-cols-3' : 'grid-cols-2',
                'md:flex md:w-auto md:min-w-0 md:grid-cols-none md:divide-x-0 md:overflow-visible md:rounded-none md:border-0 md:bg-transparent md:gap-s2',
            )}
        >
            <div className="min-w-0 overflow-hidden">
                <TagsFilter
                    tags={tags}
                    selectedTags={currentFilters.tags}
                    onApply={handleTagsChange}
                />
            </div>

            <div className="min-w-0 overflow-hidden">
                <DateRangePicker
                    value={currentFilters.dateRange}
                    onChange={handleDateChange}
                    surface="segment"
                />
            </div>

            {canFilterByAgent ? (
                <div className="min-w-0 overflow-hidden">
                    <AgentsFilter
                        users={crmUsers}
                        selectedAgentIds={displayAgentIds}
                        onApply={handleAgentsChange}
                        currentUserId={user?.id}
                    />
                </div>
            ) : null}

            <div className="hidden h-5 w-px shrink-0 bg-border md:block" aria-hidden="true" />
        </nav>
    );
};

export default FilterBar;
